export const SECTION_LABELS: Record<string, string> = {
  showNotice: "Notice Board",
  showEvent: "Events",
  showFacility: "Facilities",
  showTenantBusiness: "Tenant Business",
  showMarketplaceItem: "Marketplace",
  showService: "Services",
  showLostItem: "Lost Items",
  showFoundItem: "Found Items",
  showAnnouncement: "Announcements",
  showBuySell: "Buy/Sell",
  showFoodCourt: "Food Court",
  showRequestOffer: "Requests/Offers",
  showRetail: "Retail",
  showUrgentHelp: "Time Sensitive Help",
  showAskForHelp: "Ask for Help",
  showShareWithNeighbors: "Share with Neighbors",
  showLostFound: "Lost & Found",
  showOtherRequests: "Other Requests",
  showJob: "Jobs",
  showBuildingDocument: "Building documents",
  showWorkOrder: "Work orders",
  showBuildingSchedule: "Ops schedule",
  showBuildingSafety: "Safety hub",
  showUpkeepRequest: "Cleaning & Upkeep",
};

/** Every per-type / per-location module flag stored on LocationTypeDefinition and LocationSectionOverride. */
export const ALL_SECTION_KEYS = [
  "showNotice",
  "showEvent",
  "showFacility",
  "showTenantBusiness",
  "showMarketplaceItem",
  "showService",
  "showLostItem",
  "showFoundItem",
  "showAnnouncement",
  "showBuySell",
  "showFoodCourt",
  "showRequestOffer",
  "showRetail",
  "showUrgentHelp",
  "showAskForHelp",
  "showShareWithNeighbors",
  "showLostFound",
  "showOtherRequests",
  "showJob",
  "showBuildingDocument",
  "showWorkOrder",
  "showBuildingSchedule",
  "showBuildingSafety",
  "showUpkeepRequest",
] as const;

export type SectionKey = (typeof ALL_SECTION_KEYS)[number];

/**
 * Which module flag gates each content type. Must match the sidebar's sectionKey for the
 * module that creates that content (e.g. Retail stores MARKETPLACE_ITEM, Lost & Found stores
 * LOST_ITEM/FOUND_ITEM, Textbooks sits under Buy/Sell, Car Pooling under Services).
 * Types not listed (ACCOMMODATION) have no flag and are always enabled.
 */
export const CONTENT_TYPE_TO_SECTION_KEY: Record<string, SectionKey> = {
  NOTICE: "showNotice",
  ANNOUNCEMENT: "showNotice",
  EVENT: "showEvent",
  FACILITY: "showFacility",
  TENANT_BUSINESS: "showTenantBusiness",
  MARKETPLACE_ITEM: "showRetail",
  SERVICE: "showService",
  CARPOOL: "showService",
  LOST_ITEM: "showLostFound",
  FOUND_ITEM: "showLostFound",
  BUY_SELL: "showBuySell",
  TEXTBOOK: "showBuySell",
  FOOD_COURT: "showFoodCourt",
  REQUEST_OFFER: "showRequestOffer",
  URGENT_HELP: "showUrgentHelp",
  ASK_FOR_HELP: "showAskForHelp",
  SHARE_WITH_NEIGHBORS: "showShareWithNeighbors",
  OTHER_REQUESTS: "showOtherRequests",
  JOB: "showJob",
  BUILDING_DOCUMENT: "showBuildingDocument",
  WORK_ORDER: "showWorkOrder",
  BUILDING_SCHEDULE: "showBuildingSchedule",
  UPKEEP_REQUEST: "showUpkeepRequest",
};

/** Section keys that appear in the sidebar; used for global and per-location visibility */
export const SIDEBAR_SECTION_KEYS = [
  "showNotice",
  "showEvent",
  "showTenantBusiness",
  "showRetail",
  "showFoodCourt",
  "showFacility",
  "showService",
  "showBuySell",
  "showRequestOffer",
  "showUrgentHelp",
  "showAskForHelp",
  "showShareWithNeighbors",
  "showLostFound",
  "showOtherRequests",
  "showJob",
  "showBuildingDocument",
  "showWorkOrder",
  "showBuildingSchedule",
  "showBuildingSafety",
  "showUpkeepRequest",
] as const;

/** Map sidebar path (no leading slash in config) to section key */
export const PATH_TO_SECTION_KEY: Record<string, string> = {
  notices: "showNotice",
  events: "showEvent",
  tenants: "showTenantBusiness",
  retail: "showRetail",
  "food-court": "showFoodCourt",
  facilities: "showFacility",
  services: "showService",
  "buy-sell": "showBuySell",
  "request-offer": "showRequestOffer",
  "immediate-help": "showUrgentHelp",
  "ask-for-help": "showAskForHelp",
  "share-with-neighbors": "showShareWithNeighbors",
  "lost-found": "showLostFound",
  "other-requests": "showOtherRequests",
  jobs: "showJob",
  "building-documents": "showBuildingDocument",
  "work-orders": "showWorkOrder",
  "building-schedule": "showBuildingSchedule",
  "building-safety": "showBuildingSafety",
  upkeep: "showUpkeepRequest",
};

export const SECTION_KEYS = Object.keys(SECTION_LABELS);
